import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Descriptions, Tag, Modal, Button, Space, Input } from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { toast } from 'react-toastify';
import apiService from '../../services/ApiService';
import { STATUS_LABELS, STATUS_COLORS, TYPE_LABELS } from './PromotionColumns';
import { formatPfrDateForDisplay, formatAmountForDisplay } from './PromotionHelpers';
import '../Quiz/quizMain.css';
import '../Quiz/quizItem.css';

dayjs.extend(customParseFormat);

const { TextArea } = Input;
const PU_VERIFY_URL = 'https://suf.purs.gov.rs/verify';

const PromotionEntry = () => {
    const { id } = useParams();
    const [entry, setEntry] = useState(null);
    const [loading, setLoading] = useState(false);
    const [journalSaving, setJournalSaving] = useState(false);
    const [journal, setJournal] = useState('');
    const [selectedStatus, setSelectedStatus] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);

    const fetchEntry = useCallback(async () => {
        const [success, error] = await apiService.get(`promotion/entries/${id}`);

        if (error) {
            toast.error('Doslo je do greske, pokusajte malo kasnije');
            return;
        }

        setEntry(success?.data?.entry);
        setJournal(success?.data?.entry?.journal || '');
    }, [id]);

    useEffect(() => {
        fetchEntry();
    }, [fetchEntry]);

    const openConfirm = (status) => {
        setSelectedStatus(status);
        setConfirmOpen(true);
    };

    const handleStatusUpdate = async () => {
        setLoading(true);

        const [success, error] = await apiService.patch(`promotion/entries/${id}/status`, {
            status: selectedStatus,
        });

        setLoading(false);
        setConfirmOpen(false);

        if (error) {
            const message = error?.response?.data?.error?.message;
            toast.error(message || 'Doslo je do greske, pokusajte malo kasnije');
            return;
        }

        toast.success('Status prijave je azuriran');
        setEntry(success?.data?.entry);
        setJournal(success?.data?.entry?.journal || '');
    };

    const handleJournalSave = async () => {
        setJournalSaving(true);

        const [success, error] = await apiService.patch(`promotion/entries/${id}/journal`, {
            journal,
        });

        setJournalSaving(false);

        if (error) {
            const message = error?.response?.data?.error?.message;
            toast.error(message || 'Doslo je do greske, pokusajte malo kasnije');
            return;
        }

        toast.success('Žurnal je sačuvan');
        setEntry(success?.data?.entry);
        setJournal(success?.data?.entry?.journal || '');
    };

    if (!entry) {
        return (
            <div className="quizMain quizItem">
                <div className="title">Detalj prijave</div>
            </div>
        );
    }

    const reviewerName = entry.reviewedBy
        ? [entry.reviewedBy.firstName, entry.reviewedBy.lastName].filter(Boolean).join(' ') ||
          entry.reviewedBy.email
        : null;

    return (
        <div className="quizMain quizItem">
            <div className="title">Detalj prijave</div>

            <Descriptions
                bordered
                column={1}
                className="custom-description"
                extra={
                    entry.type !== 'automatic' ? (
                        <Space wrap>
                            <Button type="primary" onClick={() => openConfirm('valid')}>
                                Valid
                            </Button>
                            <Button danger onClick={() => openConfirm('invalid')}>
                                Invalid
                            </Button>
                            <Button onClick={() => openConfirm('pending')}>
                                Pending
                            </Button>
                        </Space>
                    ) : null
                }
            >
                <Descriptions.Item label="PFR">{entry.pfr}</Descriptions.Item>

                <Descriptions.Item label="Tip">{TYPE_LABELS[entry.type] || entry.type}</Descriptions.Item>

                <Descriptions.Item label="Status">
                    <Tag color={STATUS_COLORS[entry.status]}>
                        {STATUS_LABELS[entry.status] || entry.status}
                    </Tag>
                </Descriptions.Item>

                <Descriptions.Item label="Email">{entry.user?.email || '-'}</Descriptions.Item>

                <Descriptions.Item label="Telefon">{entry.user?.phone || '-'}</Descriptions.Item>

                <Descriptions.Item label="Datum prijave">
                    {entry.submittedAt ? dayjs(entry.submittedAt).format('D MMM YYYY HH:mm') : '-'}
                </Descriptions.Item>

                {entry.type === 'automatic' && (
                    <Descriptions.Item label="Poreski link">
                        {entry.taxLink ? (
                            <a href={entry.taxLink} target="_blank" rel="noopener noreferrer">
                                Otvori poreski servis
                            </a>
                        ) : (
                            '-'
                        )}
                    </Descriptions.Item>
                )}

                {entry.type === 'manual' && (
                    <>
                        <Descriptions.Item label="Brojac">{entry.receiptCounter || '-'}</Descriptions.Item>
                        <Descriptions.Item label="Iznos">
                            {entry.totalAmount != null ? formatAmountForDisplay(entry.totalAmount) : '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Datum PFR">
                            {formatPfrDateForDisplay(entry.pfrDate)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Vreme PFR">{entry.pfrTime || '-'}</Descriptions.Item>
                        <Descriptions.Item label="Poreski link">
                            <a href={PU_VERIFY_URL} target="_blank" rel="noopener noreferrer">
                                Otvori poreski servis
                            </a>
                        </Descriptions.Item>
                    </>
                )}

                {entry.reviewedAt && (
                    <Descriptions.Item label="Pregledano">
                        {dayjs(entry.reviewedAt).format('D MMM YYYY HH:mm')}
                        {reviewerName ? ` - ${reviewerName}` : ''}
                    </Descriptions.Item>
                )}
            </Descriptions>

            {entry.type === 'manual' && (
                <div className="entry-journal">
                    <div className="entry-journal-title">Žurnal</div>
                    <p className="entry-journal-hint">
                        Tekst sa portala Poreske uprave (sekcija "Žurnal") nakon provere računa.
                    </p>
                    <TextArea
                        id="entryJournal"
                        className="entry-journal-textarea"
                        rows={16}
                        value={journal}
                        onChange={(e) => setJournal(e.target.value)}
                        placeholder={
                            '============ FISKALNI RACUN ============\n...\nNalepite žurnal sa suf.purs.gov.rs'
                        }
                    />
                    <Button type="primary" onClick={handleJournalSave} loading={journalSaving}>
                        Sačuvaj
                    </Button>
                </div>
            )}

            <Modal
                title="Potvrda promene statusa"
                open={confirmOpen}
                onOk={handleStatusUpdate}
                onCancel={() => setConfirmOpen(false)}
                okText="Potvrdi"
                cancelText="Otkazi"
                confirmLoading={loading}
            >
                Da li ste sigurni da zelite da postavite status na{' '}
                <strong>{STATUS_LABELS[selectedStatus] || selectedStatus}</strong>?
            </Modal>
        </div>
    );
};

export default PromotionEntry;
